import { useEffect, useState } from "react";
import { Box, Button, Link, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { Calculate } from "@mui/icons-material";
import { Editor, useMonaco, type OnMount } from "@monaco-editor/react";
import type { editor as editorNs } from "monaco-editor";
import { setupFilterLanguage } from "./filterMonaco";
import { ValidationStatusChip, type ValidationStatus } from "./ValidationStatusChip";
import type { EstimateState } from "./useFilterEstimate";

interface Props {
  // Which corpora the wizard is targeting — gates the example list so
  // podcast-only fields aren't suggested for a broadcast-only export and
  // vice versa. A mixed set shows the union of both.
  contentTypes: string[];
  value: string;
  onChange: (v: string) => void;
  // Estimate plumbing — owned by ExportNew so the funnel breadcrumb
  // and this step's button stay in sync. The button is enabled only
  // when there's a meaningful change to estimate (non-empty filter,
  // months selected, not currently running).
  estimate: EstimateState;
  estimateStale: boolean;
  canEstimate: boolean;
  onEstimate: () => void;
}

interface ValidateResponse {
  ok: boolean;
  message?: string;
  line?: number;
  column?: number;
  length?: number;
}

// Step 2 — filter expression, edited in a Monaco-powered editor.
//
// Beyond syntax highlighting + completions:
//   * On every (debounced) keystroke we POST the expression to
//     /api/v1/filter/validate. On error, the response carries
//     line/column/length and we render a Monaco marker (red squiggle
//     + tooltip). The portal uses paim.filter_lang.validate_filter so
//     the diagnostics match what compile_filter would say at submit.
//   * The same response drives a status chip below the editor — at-a-
//     glance indicator that doesn't require eyeballing for squigglies.
//   * Validation is schema-less — column-existence is not checked here;
//     the user finds out about an unknown column at submit time.

const VALIDATE_DEBOUNCE_MS = 350;
const MARKER_OWNER = "paim-filter-validator";

export function StepFilter({
  contentTypes,
  value,
  onChange,
  estimate,
  estimateStale,
  canEstimate,
  onEstimate,
}: Props) {
  const muiTheme = useTheme();
  const monaco = useMonaco();
  const [editor, setEditor] = useState<editorNs.IStandaloneCodeEditor | null>(null);
  const [status, setStatus] = useState<ValidationStatus>(
    value.trim() ? { kind: "checking" } : { kind: "empty" },
  );

  useEffect(() => {
    if (!monaco) return;
    setupFilterLanguage(monaco);
  }, [monaco]);

  // Debounced server-side validation → Monaco markers + status chip.
  useEffect(() => {
    if (!monaco || !editor) return;
    const model = editor.getModel();
    if (!model) return;

    // Empty-filter shortcut: don't bother the server, no markers needed.
    if (!value.trim()) {
      monaco.editor.setModelMarkers(model, MARKER_OWNER, []);
      setStatus({ kind: "empty" });
      return;
    }

    setStatus({ kind: "checking" });
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const r = await fetch("/api/v1/filter/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filter: value }),
          signal: controller.signal,
        });
        if (!r.ok) return;
        const data: ValidateResponse = await r.json();
        if (data.ok) {
          monaco.editor.setModelMarkers(model, MARKER_OWNER, []);
          setStatus({ kind: "ok" });
          return;
        }
        const line = data.line ?? 1;
        const column = data.column ?? 1;
        const length = data.length ?? 1;
        const message = data.message ?? "invalid filter";
        monaco.editor.setModelMarkers(model, MARKER_OWNER, [
          {
            severity: monaco.MarkerSeverity.Error,
            message,
            startLineNumber: line,
            startColumn: column,
            endLineNumber: line,
            endColumn: column + length,
            source: "paim-filter",
          },
        ]);
        setStatus({ kind: "error", message });
      } catch {
        // Network blip / abort — leave the previous status alone; next keystroke retries.
      }
    }, VALIDATE_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [value, monaco, editor]);

  const handleMount: OnMount = (e) => setEditor(e);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Filter the page-level metadata. Runs against the rollup index — DuckDB pushes the
        predicate down so it's fast. Press Ctrl+Space to summon completions on demand;
        functions insert with placeholders you can Tab through. Errors surface as red
        squigglies in real time.
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}`,
          },
        }}
      >
        <Editor
          height={120}
          language="paim-filter"
          value={value}
          onChange={(v) => onChange(v ?? "")}
          onMount={handleMount}
          theme={muiTheme.palette.mode === "dark" ? "vs-dark" : "vs"}
          options={{
            minimap: { enabled: false },
            lineNumbers: "off",
            folding: false,
            glyphMargin: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
            scrollbar: { vertical: "hidden", horizontal: "auto" },
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            wordWrap: "on",
            scrollBeyondLastLine: false,
            renderLineHighlight: "none",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 14,
            padding: { top: 12, bottom: 12 },
            suggestOnTriggerCharacters: true,
            quickSuggestions: { other: true, comments: false, strings: false },
            automaticLayout: true,
            tabCompletion: "on",
          }}
        />
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mt: 1.5, flexWrap: "wrap" }}
      >
        <ValidationStatusChip
          status={status}
          emptyLabel="No query filter"
          okLabel="Query filter okay"
          errorPrefix="Query filter error"
        />
        <Box flexGrow={1} />
        {(() => {
          const running = estimate.kind === "running";
          let label = "Estimate matches";
          if (running) label = "Estimating…";
          else if (estimate.kind === "ok" && !estimateStale)
            label = "Re-estimate";
          else if (estimate.kind === "ok" && estimateStale)
            label = "Refresh estimate";
          else if (estimate.kind === "error") label = "Retry estimate";
          return (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Calculate />}
              onClick={onEstimate}
              disabled={!canEstimate || running}
            >
              {label}
            </Button>
          );
        })()}
      </Stack>
      <FilterExamples onPick={onChange} contentTypes={contentTypes} />
    </Box>
  );
}

// Clickable example filters. Each row sets the editor's value when
// clicked; the expression is rendered as a <code> span so the user
// can also select-and-copy without picking. Examples are tuned to
// the production rollup schema verified 2026-05-29 (see
// pages_20260505*.parquet): media_type ∈ {tv, radio}, no `audience`
// column, no cable/broadcast distinction at the media_type level —
// "cable" surfaced via a known station-guid allow-list.
interface FilterExample {
  label: string;
  expr: string;
  note?: string;
  // Which corpus this example applies to. Omitted ⇒ applies to both
  // (works against the union schema regardless of content type).
  contentType?: "broadcast" | "podcast";
}

const FILTER_EXAMPLES: FilterExample[] = [
  {
    label: "Single source",
    expr: "source_id == 'CNN'",
    contentType: "broadcast",
  },
  {
    label: "Multiple sources",
    expr: "source_id in ('CNN', 'FNC', 'MSNBC')",
    contentType: "broadcast",
  },
  {
    label: "US only",
    expr: "market_country == 'US'",
    contentType: "broadcast",
  },
  {
    label: "By US state",
    expr: "market_country == 'US' and market_state == 'NY'",
    contentType: "broadcast",
  },
  {
    label: "US cable",
    expr: "market_country == 'US' and market_name == 'U.S. Cable'",
    contentType: "broadcast",
  },
  {
    label: "US radio",
    expr: "market_country == 'US' and media_type == 'radio'",
    note: "media_type values in the rollup are 'tv' and 'radio'.",
    contentType: "broadcast",
  },
  {
    label: "Top 10 DMAs",
    expr: "dma_rank <= 10 and market_country == 'US'",
    contentType: "broadcast",
  },
  {
    label: "Canada or US cable",
    expr:
      "market_country == 'CA' or (market_country == 'US' and market_name == 'U.S. Cable')",
    note: "OR combines two scopes — parentheses group the AND so the precedence reads naturally.",
    contentType: "broadcast",
  },
  {
    label: "News programs",
    expr:
      "has(categories, 'News') or has(categories, 'Noticias') or has(categories, 'Nachrichten')",
    note: "categories is list-valued — use has(categories, 'x') for membership rather than ==. Works for broadcast and podcast alike.",
  },
  {
    label: "Broadcast only",
    expr: "source_type == 'broadcast'",
    note: "source_type is 'broadcast' or 'omcs-podcast' — the discriminator for mixed datasets. Use this to keep only broadcast in a mixed export.",
    contentType: "broadcast",
  },
  {
    label: "Podcasts only",
    expr: "source_type == 'omcs-podcast'",
    note: "source_type is 'broadcast' or 'omcs-podcast' — the discriminator for mixed datasets.",
    contentType: "podcast",
  },
  {
    label: "English podcasts",
    expr: "source_type == 'omcs-podcast' and language == 'en'",
    contentType: "podcast",
  },
  {
    label: "Explicit episodes",
    expr: "source_type == 'omcs-podcast' and explicit == true",
    contentType: "podcast",
  },
  {
    label: "One podcast show",
    expr: "source_name == 'Roderick on the Line'",
    note: "For podcasts, source_name is the show/feed title and source_id its feed id.",
    contentType: "podcast",
  },
];

function FilterExamples({
  onPick,
  contentTypes,
}: {
  onPick: (expr: string) => void;
  contentTypes: string[];
}) {
  // Show an example when it's unscoped (works against the union) or its
  // corpus is one the run targets.
  const examples = FILTER_EXAMPLES.filter(
    (ex) =>
      ex.contentType === undefined || contentTypes.includes(ex.contentType),
  );
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Examples — click to load into the editor; select-and-copy works too.
      </Typography>
      <Stack
        component="ul"
        spacing={0.5}
        sx={{ listStyle: "none", pl: 0, mt: 0.5, mb: 0 }}
      >
        {examples.map((ex) => (
          <Box
            key={ex.label}
            component="li"
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 140, flexShrink: 0 }}
            >
              {ex.label}
            </Typography>
            <Tooltip title={ex.note ?? "Click to load into the editor"} placement="top-start">
              <Link
                component="button"
                type="button"
                onClick={() => onPick(ex.expr)}
                underline="hover"
                sx={{
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: "0.85rem",
                  textAlign: "left",
                  color: "text.primary",
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                  // Keep <code> styling so the expression reads as
                  // copy-pastable text, even though it's a button.
                  px: 0.75,
                  py: 0.25,
                  bgcolor: "action.hover",
                  borderRadius: 0.5,
                }}
              >
                {ex.expr}
              </Link>
            </Tooltip>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
